// Avatar color palette for consistent user colors
export const AVATAR_COLORS = [
  'dark-red', 'cranberry', 'red', 'pumpkin', 'peach', 'marigold', 'gold', 'brass',
  'brown', 'forest', 'seafoam', 'dark-green', 'light-teal', 'teal', 'steel', 'blue',
  'royal-blue', 'cornflower', 'navy', 'lavender', 'purple', 'grape', 'lilac', 'pink',
  'magenta', 'plum', 'beige', 'mink', 'anchor'
] as const;

export type AvatarColor = typeof AVATAR_COLORS[number];

// Get initials from a name (matches FluentUI Avatar's logic)
export const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

// Hash function for generating initial color index
export const hashString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return hash;
};

// Assign colors to users ensuring same-initial users get different colors
export const assignAvatarColors = (users: Array<{id: string, displayName: string}>): Map<string, AvatarColor> => {
  const colorMap = new Map<string, AvatarColor>();
  const initialGroups = new Map<string, Array<{id: string, displayName: string}>>();

  // Group users by initials
  users.forEach(user => {
    const initials = getInitials(user.displayName);
    if (!initialGroups.has(initials)) {
      initialGroups.set(initials, []);
    }
    initialGroups.get(initials)!.push(user);
  });

  // Assign colors ensuring same-initial users get different colors
  initialGroups.forEach((groupUsers) => {
    const usedColorIndices = new Set<number>();

    // Sort users by ID for consistency
    groupUsers.sort((a, b) => a.id.localeCompare(b.id));

    groupUsers.forEach((user) => {
      // Start with the hash-based color index
      let colorIndex = Math.abs(hashString(user.id)) % AVATAR_COLORS.length;

      // If this color is already used by someone with same initials, find next available
      while (usedColorIndices.has(colorIndex)) {
        colorIndex = (colorIndex + 1) % AVATAR_COLORS.length;
      }

      usedColorIndices.add(colorIndex);
      colorMap.set(user.id, AVATAR_COLORS[colorIndex]);
    });
  });

  return colorMap;
};

// Fallback for when we don't have the full user list context
export const getUserAvatarColor = (userId: string): AvatarColor => {
  const index = Math.abs(hashString(userId)) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
};
